// Angular
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  NgZone,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Ionic
import {
  AlertController,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';

// Capacitor
import { Capacitor } from '@capacitor/core';

// Third party
import { NativeSettings, AndroidSettings } from 'capacitor-native-settings';

// Local library
import { WagerFacade } from '@amwager/wager/data-access';
import { AuthFacade } from '@amwager/authentication/data-access';
import { CommonFacade, PermissionService, SessionService } from '@amwager/data-access';
import { AccountFacade } from '@amwager/account/data-access';
import { AccountSettingsComponent } from '../account-settings/account-settings.component';
import { FundingComponent } from '@amwager/funding/feature-ion';
import { GeoLocationService, NumberWithCommasPipe } from '@amwager/util';
import { AccountHistoryComponent } from '../account-history/account-history.component';
import { CommonModule } from '@angular/common';
import { ResponsibleGamingComponent } from '../responsible-gaming/responsible-gaming.component';
import { ResultsMenuComponent } from '@amwager/wager/feature-ion';
import { GUEST, IGeoLocationModel } from '@amwager/util-ts';

@Component({
  selector: 'feature-ion-amwager3-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    IonContent,
    IonItem,
    IonInput,
    IonIcon,
    NumberWithCommasPipe,
    FormsModule,
    CommonModule,
    ResponsibleGamingComponent
  ],
  standalone: true
})
export class ProfileComponent implements OnInit {

  geoLocation!: IGeoLocationModel;
  responsibleGaming:boolean = false;

  userName = '';
  userIcon = '';
  balance!: number;
  @HostBinding('class.some-class') someClass = true;

  private showAlertInfoHandler?: HTMLIonAlertElement;
  private showAlertErrorHandler?: HTMLIonAlertElement;

  constructor(
    private modalCtrl: ModalController,
    private elementRef: ElementRef,
    private readonly sessionService: SessionService,
    private readonly permissionService: PermissionService,
    private geoLocationService: GeoLocationService,
    private readonly wagerFacade: WagerFacade,
    private readonly authFacade: AuthFacade,
    private readonly accountFacade: AccountFacade,
    private readonly alertCtrl: AlertController,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly commonFacade: CommonFacade,
  ) {}

  ngOnInit() {

    this.wagerFacade.getTicketGeoLocation$().subscribe((response?: IGeoLocationModel) => {
      if (response === undefined) return;
      this.geoLocation = response;
    });

    this.userName = this.sessionService.getUserFName() + ' ' + this.sessionService.getUserLast();
    this.userIcon = this.sessionService.getUserFName().charAt(0) + ' ' +
                    this.sessionService.getUserLast().charAt(0);

    this.balance = 0;
    this.accountFacade.getBalance$().subscribe((balance) => {
      if (!balance) return;

      this.balance = balance;
      this.cdRef.markForCheck();
    });

    this.authFacade.isLogoutSuccess$().subscribe((isLogout?: boolean) => {
      if (isLogout) {
        this.modalCtrl.dismiss();

        this.alertCtrl?.dismiss();
        this.showAlertInfoHandler?.dismiss();
        this.showAlertErrorHandler?.dismiss();

        this.cdRef.markForCheck();
      }
    });
  }

  async openFunding(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openFunding', false);
    }

    if (!this.hasPermission()) {
      this.showAlertInfo('You are not an Identified User.');
      return;
    }

    if (this.geoLocation === undefined) {
      const result = await this.geoLocationService.askPermissionGeoLocation();

      if ('latitude' in result && 'longitude' in result) {
        if (result === undefined)
          return;

        if (result.latitude && result.longitude) {
          this.wagerFacade.setGeoLocation({
            CLat: result.latitude, CLon: result.longitude
          },
          'ProfileComponent:openFunding');
        }

        const modal = await this.modalCtrl.create({
          component: FundingComponent,
        });
        modal.present();

        return;

      }
      else
        if (result.showDeviceSetting)
          this.showAlertError(result.msg, 'Yes');
        else
          this.showAlertError(result.msg, '');
        return;
    }

    const modal = await this.modalCtrl.create({
      component: FundingComponent,
    });
    modal.present();
  }

  async openProfile() {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openProfile', false);
    }

    this.showAlertInfo('The countdown to your next big win starts soon!');
  }

  async openAccountSettings(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openAccountSettings', false);
      return;
    }

    const modal = await this.modalCtrl.create({
      component: AccountSettingsComponent,
    });
    modal.present();
  }

  async openAccountHistory(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openAccountHistory', false);
      return;
    }

    if (!this.hasPermission()) {

      // encase
      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.showAlertInfo('You are not an Identified User.');
      }

      return;
    }

    const modal = await this.modalCtrl.create({
      component: AccountHistoryComponent,
    });
    modal.present();
  }

  async openResponsibleGaming(): Promise<void> {
    if (!this.hasPermission()) {

      // encase
      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.showAlertInfo('You are not an Identified User.');
      }

      return;
    }

    this.responsibleGaming = !this.responsibleGaming;

    // const modal = await this.modalCtrl.create({
    //   component: ResponsibleGamingComponent,
    // });
    // modal.present();
  }

  async openBetResults(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openBetResults', false);
      return;
    }

    const modal = await this.modalCtrl.create({
      component: ResultsMenuComponent,
    });
    modal.present();
  }

  async openPromo() {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openPromo', false);
    }
    this.showAlertInfo('The countdown to your next big win starts soon!');
  }

  async openHelp() {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:openHelp', false);
    }
    this.showAlertInfo('The countdown to your next big win starts soon!');
  }

  hasPermission(): boolean {
    if (this.permissionService.isAllowed()) {
      return true;
    }

    return false;
  }

  async showAlertInfo(msg: string): Promise<void> {
    this.showAlertInfoHandler = await this.alertCtrl.create({
      header: 'Info',
      message: msg,
      buttons: [
        {
          text: 'Close',
          handler: () => {
            // comment out for now
            // this.ngZone.run(()=>{
            //   this.modalCtrl.dismiss();
            // });
          }
        }
      ]
    });

    await this.showAlertInfoHandler.present();
  }

  async logout(): Promise<void> {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ProfileComponent:logout', false);
      return;
    }

    const alert = await this.alertCtrl.create({
      header: 'Confirm',
      message: 'Are you sure you want to log out?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
          }
        },
        {
          text: 'Yes',
          handler: () => {
            this.ngZone.run(()=>{
              this.authFacade.logout();
              // this.modalCtrl.dismiss();
            });
          }
        }
      ]
    });

    await alert.present();
  }

  async showAlertError(error: string, btn: string): Promise<void> {

    const platform = Capacitor.getPlatform();

    this.showAlertErrorHandler = await this.alertCtrl.create({
      header: 'Error',
      message: error,
      buttons: [
        {
          text: 'Close',
          handler: () => {
            this.ngZone.run(()=>{
              this.modalCtrl.dismiss();
            });
          }
        },
        {
          text: btn,
          handler: () => {
            this.ngZone.run(()=>{
              if (platform === 'android') {
                NativeSettings.openAndroid({
                  option: AndroidSettings.Location,
                });
              }
              this.modalCtrl.dismiss();
            });
          }
        }
      ]
    });

    await this.showAlertErrorHandler.present();
  }

  cancel() {
    this.modalCtrl.dismiss();
  }

  close() {
    this.modalCtrl.dismiss();
  }

}
